import React, { useState, useEffect } from "react";
import { X, Users, CheckCircle, Clock, Award, Shield, RefreshCw, Search, Key } from "react-feather";
import { collection, query, where, getDocs, doc, setDoc } from "firebase/firestore";
import { db } from "shared/utils/firebase";
import styles from "./EventAttendanceRosterModal.module.css";

const EventAttendanceRosterModal = ({ event, onClose }) => {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchRoster = async () => {
    if (!event?.id) return;
    setLoading(true);
    try {
      const q = query(collection(db, "eventRegistrations"), where("eventId", "==", event.id));
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRoster(list);
    } catch (err) {
      console.error("Error fetching event roster:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoster();
  }, [event]);

  const toggleAttendanceStatus = async (item) => {
    const nextStatus = item.status === "attended" ? "registered" : "attended";
    const now = new Date();
    try {
      const regRef = doc(db, "eventRegistrations", item.id);
      await setDoc(regRef, {
        status: nextStatus,
        eligibleForCertificate: nextStatus === "attended",
        attendedAt: now,
        updatedAt: now,
        lastActiveAt: now
      }, { merge: true });

      setRoster(prev => prev.map(r => r.id === item.id ? { 
        ...r, 
        status: nextStatus, 
        eligibleForCertificate: nextStatus === "attended",
        attendedAt: now,
        updatedAt: now,
        lastActiveAt: now 
      } : r));
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const getCleanTicketCode = (item) => {
    if (item.ticketCode) return item.ticketCode;
    if (item.id) {
      const parts = item.id.split("_");
      return "PASS-" + (parts[parts.length - 1] || item.id).slice(0, 8).toUpperCase();
    }
    return "PASS-UNKNOWN";
  };

  const filteredRoster = roster.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase().trim();
    const passCode = getCleanTicketCode(item).toLowerCase();
    const userName = (item.userName || "").toLowerCase();
    const userEmail = (item.userEmail || "").toLowerCase();
    const regId = (item.id || "").toLowerCase();
    return passCode.includes(term) || userName.includes(term) || userEmail.includes(term) || regId.includes(term);
  });

  if (!event) return null;

  const totalRegistered = roster.length;
  const totalAttended = roster.filter(r => r.status === "attended").length;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h3 className={styles.title}>📋 Attendance Roster & Pass Verification</h3>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              Event: <strong>{event.title}</strong> | Mode: <strong>{event.eventMode || 'Online'}</strong>
            </span>
          </div>
          <button className={styles.closeBtn} onClick={onClose} title="Close Modal">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Quick Summary Cards & Search Bar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '1rem' }}>
            <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#2563eb', fontWeight: '700' }}>Total Registrations</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1e293b' }}>{totalRegistered}</div>
            </div>
            <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#059669', fontWeight: '700' }}>Verified Attendees</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1e293b' }}>{totalAttended}</div>
            </div>
            <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#7c3aed', fontWeight: '700' }}>Attendance Rate</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1e293b' }}>
                {totalRegistered > 0 ? Math.round((totalAttended / totalRegistered) * 100) : 0}%
              </div>
            </div>
          </div>

          {/* Pass Verification Search Bar */}
          <div style={{ position: 'relative', marginBottom: '1.25rem' }}>
            <Search size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
            <input
              type="text"
              placeholder="Verify Pass Code (e.g. PASS-789234), Name, or Email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px 10px 40px',
                borderRadius: '8px',
                border: '1.5px solid var(--border-color, #cbd5e1)',
                backgroundColor: 'var(--input-bg, #ffffff)',
                fontSize: '0.9rem',
                color: 'var(--text-primary, #0f172a)'
              }}
            />
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <RefreshCw size={24} className="spin-animation" style={{ marginBottom: '8px' }} />
              <p>Loading attendance records from Firestore...</p>
            </div>
          ) : filteredRoster.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <Users size={36} style={{ marginBottom: '8px', color: '#94a3b8' }} />
              <p>{searchTerm ? `No matching Pass Code or participant found for "${searchTerm}"` : 'No registrations recorded yet for this event.'}</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Pass Code</th>
                  <th>Participant</th>
                  <th>Email</th>
                  <th>Active Minutes</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoster.map((item) => {
                  const passCodeStr = getCleanTicketCode(item);
                  return (
                    <tr key={item.id}>
                      <td>
                        <span style={{ fontFamily: 'monospace', padding: '3px 8px', borderRadius: '6px', backgroundColor: 'rgba(16, 185, 129, 0.12)', color: '#059669', fontWeight: '800', fontSize: '0.85rem' }}>
                          🔑 {passCodeStr}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600' }}>{item.userName || "User"}</div>
                        {item.customAnswers?.teamName && (
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Team: {item.customAnswers.teamName}</span>
                        )}
                      </td>
                      <td>{item.userEmail || "N/A"}</td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                          <Clock size={13} style={{ color: '#3b82f6' }} />
                          {item.activeMinutes || 0} mins
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[item.status] || styles.registered}`}>
                          {item.status || 'registered'}
                        </span>
                      </td>
                      <td>
                        <button
                          className={styles.actionBtn}
                          onClick={() => toggleAttendanceStatus(item)}
                        >
                          {item.status === 'attended' ? 'Mark Registered' : 'Mark Attended ✓'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
            Verify student tickets by typing their Pass Code into the search bar above.
          </span>
          <button className={styles.actionBtn} onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventAttendanceRosterModal;
